import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogIn, AlertCircle } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const dest = location.state?.from?.pathname || '/dashboard';
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gradient-to-br from-tw1-brand-navy-top to-tw1-brand-navy font-sans px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center text-white">
          <img 
            src="/images/logo.png" 
            alt="Truckwash1" 
            className="h-14 w-auto object-contain mb-4"
          />
          <h1 className="text-3xl font-bold tracking-tight">SmartRoster</h1>
          <p className="mt-1 text-sm text-white/60">Workforce Management</p>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-slate-900 mb-6 text-center">Inloggen</h2>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                E-mail
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                data-testid="login-email"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none focus:ring-2 focus:ring-tw1-accent-gold/30"
                placeholder="jouwnaam@truckwash1group.nl"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Wachtwoord
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="login-password"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none focus:ring-2 focus:ring-tw1-accent-gold/30"
              />
            </div>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="flex w-full items-center justify-center gap-2 rounded-md bg-tw1-accent-gold px-4 py-2.5 text-sm font-bold text-tw1-brand-navy shadow hover:bg-tw1-accent-gold-dark disabled:opacity-60 transition-colors"
            >
              <LogIn size={16} />
              {submitting ? 'Bezig met inloggen…' : 'Inloggen'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            <a href="/forgot-password" className="font-medium text-tw1-brand-navy hover:underline" data-testid="link-forgot-password">
              Wachtwoord vergeten?
            </a>
          </p>
          <p className="mt-3 text-center text-xs text-slate-500">
            Medewerker?{' '}
            <a href="/portaal/login" className="font-medium text-tw1-brand-navy hover:underline" data-testid="link-portal-login">
              Klik hier voor het portaal
            </a>
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-white/40">
          v1.1.0 · SmartRoster · Fase 1 (Foundation)
        </p>
      </div>
    </div>
  );
}
